import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import styled from "styled-components";
import {
  Card,
  Button,
  message,
  Spin,
  Alert,
  Typography,
  Space,
  Tag,
} from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  BankOutlined,
  LinkOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { getRentPayment } from "../lib/firebase/paymentModel";
import { getPaymentStatus } from "../lib/payments/stripe";
import Logo from "../components/general/Logo";

const { Title, Text, Paragraph } = Typography;

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 2rem 1rem;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const Content = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const StatusCard = styled(Card)`
  border-radius: 16px;
  border: 2px solid #e2e8f0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  background: white;
  margin-bottom: 1.5rem;

  .ant-card-body {
    padding: 2rem;
  }
`;

const StatusIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  margin: 0 auto 1.5rem auto;

  .anticon {
    font-size: 40px;
  }

  &.success {
    background: rgba(34, 197, 94, 0.1);
    color: #22c55e;
  }

  &.processing {
    background: rgba(245, 158, 11, 0.1);
    color: #f59e0b;
  }

  &.verification {
    background: rgba(30, 58, 138, 0.1);
    color: #1e3a8a;
  }

  &.error {
    background: rgba(239, 68, 68, 0.1);
    color: #ef4444;
  }
`;

const RentPaymentStatus = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [refreshing, setRefreshing] = useState(false);

  // Fetch payment details
  const {
    data: payment,
    isLoading: paymentLoading,
    refetch: refetchPayment,
  } = useQuery({
    queryKey: ["payment", id],
    queryFn: async () => {
      const result = await getRentPayment(id);
      return result.success ? result.data : null;
    },
    enabled: !!id,
    refetchInterval: 30000, // Refetch every 30 seconds to check for status updates
  });

  // Fetch canonical payment status from backend
  const {
    data: statusData,
    isLoading: statusLoading,
    refetch: refetchStatus,
  } = useQuery({
    queryKey: ["paymentStatus", id, payment?.stripePaymentIntentId],
    queryFn: async () => {
      if (!payment?.stripePaymentIntentId && !id) return null;
      try {
        return await getPaymentStatus({
          rentPaymentId: id,
          paymentIntentId: payment?.stripePaymentIntentId,
        });
      } catch (error) {
        console.error("[RentPaymentStatus] Error fetching status:", error);
        return null;
      }
    },
    enabled: !!id && (!!payment?.stripePaymentIntentId || !!payment),
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all([refetchPayment(), refetchStatus()]);
      message.success("Status updated");
    } catch (error) {
      message.error("Failed to refresh status");
    } finally {
      setRefreshing(false);
    }
  };

  const isLoading = paymentLoading || statusLoading;
  const effectiveStatus = statusData?.status || payment?.status;
  const nextAction = statusData?.next_action || payment?.next_action;
  const verificationUrl =
    statusData?.verificationUrl || payment?.verificationUrl;

  // Adjusted amount due (base + adjustments) for display
  const getAdjustedAmountDue = (p) => {
    if (!p) return 0;
    const base = p.amount ?? p.amountDue ?? 0;
    const sum = (p.adjustments || []).reduce((s, a) => s + (a?.amount ?? 0), 0);
    return base + sum;
  };
  const displayAmount = payment ? getAdjustedAmountDue(payment) : 0;

  // Log status for debugging (non-prod only)
  if (process.env.NODE_ENV !== "production") {
    console.log("[RentPaymentStatus] Payment:", payment);
    console.log("[RentPaymentStatus] Status data:", statusData);
    console.log("[RentPaymentStatus] Effective status:", effectiveStatus);
    console.log("[RentPaymentStatus] Next action:", nextAction);
  }

  if (isLoading && !payment) {
    return (
      <Container>
        <Content>
          <Spin
            size="large"
            style={{ display: "block", textAlign: "center", marginTop: "4rem" }}
          />
        </Content>
      </Container>
    );
  }

  if (!payment) {
    return (
      <Container>
        <Content>
          <StatusCard>
            <Alert
              message="Payment Not Found"
              description="The payment you're looking for could not be found."
              type="error"
              showIcon
            />
            <Button
              type="primary"
              onClick={() => navigate("/")}
              style={{ marginTop: "1rem" }}
            >
              Go Home
            </Button>
          </StatusCard>
        </Content>
      </Container>
    );
  }

  // Determine UI state based on status
  let statusConfig = {
    icon: <ClockCircleOutlined />,
    title: "Checking payment status...",
    description: "Please wait while we check your payment status.",
    type: "processing",
    showVerificationButton: false,
    showRetryButton: false,
  };

  if (effectiveStatus === "succeeded" || payment.status === "paid") {
    statusConfig = {
      icon: <CheckCircleOutlined />,
      title: "Payment Successful",
      description: "Your rent payment has been processed successfully.",
      type: "success",
      showVerificationButton: false,
      showRetryButton: false,
    };
  } else if (
    effectiveStatus === "requires_action" &&
    nextAction?.type === "verify_with_microdeposits"
  ) {
    statusConfig = {
      icon: <BankOutlined />,
      title: "Verify your bank account",
      description:
        "Your bank requires verification via microdeposits. Stripe will send two small deposits (less than $1 each) to your bank account. These typically appear within 1-2 business days. Check your bank statement, then return to Stripe's verification page to enter the amounts.",
      type: "verification",
      showVerificationButton: true,
      showRetryButton: false,
    };
  } else if (
    effectiveStatus === "processing" ||
    payment.status === "processing"
  ) {
    statusConfig = {
      icon: <ClockCircleOutlined />,
      title: "Payment processing",
      description:
        "ACH payments can take a few business days to complete. You'll receive a confirmation email once the payment is processed.",
      type: "processing",
      showVerificationButton: false,
      showRetryButton: false,
    };
  } else if (
    effectiveStatus === "requires_payment_method" ||
    effectiveStatus === "payment_failed" ||
    payment.status === "failed"
  ) {
    statusConfig = {
      icon: <ExclamationCircleOutlined />,
      title: "Payment Failed",
      description:
        statusData?.error ||
        payment?.error ||
        "Your payment could not be processed. Please try again with a different payment method.",
      type: "error",
      showVerificationButton: false,
      showRetryButton: true,
    };
  } else if (
    payment.status === "pending_verification" ||
    payment.status === "PENDING_VERIFICATION"
  ) {
    statusConfig = {
      icon: <BankOutlined />,
      title: "Verify your bank account",
      description:
        "Your bank requires verification via microdeposits. Stripe will send two small deposits (less than $1 each) to your bank account. These typically appear within 1-2 business days. Check your bank statement, then return to Stripe's verification page to enter the amounts.",
      type: "verification",
      showVerificationButton: true,
      showRetryButton: false,
    };
  }

  return (
    <Container>
      <Header>
        <Logo size="large" displayMode="both" color="black" linkTo={null} />
        <Text type="secondary">Payment Status</Text>
      </Header>

      <Content>
        <StatusCard>
          <StatusIcon className={statusConfig.type}>
            {statusConfig.icon}
          </StatusIcon>

          <Title
            level={3}
            style={{ textAlign: "center", marginBottom: "1rem" }}
          >
            {statusConfig.title}
          </Title>

          <Paragraph
            style={{
              textAlign: "center",
              fontSize: "16px",
              color: "#666",
              marginBottom: "2rem",
            }}
          >
            {statusConfig.description}
          </Paragraph>

          {statusConfig.showVerificationButton && verificationUrl && (
            <>
              <Button
                type="primary"
                size="large"
                icon={
                  <LinkOutlined style={{ filter: "brightness(0) invert(1)" }} />
                }
                onClick={() => {
                  window.open(verificationUrl, "_blank", "noopener,noreferrer");
                }}
                style={{
                  width: "100%",
                  height: "48px",
                  backgroundColor: "var(--color-primary)",
                  borderColor: "var(--color-primary)",
                  marginBottom: "1rem",
                }}
              >
                Verify now
              </Button>
              <Text
                type="secondary"
                style={{
                  display: "block",
                  textAlign: "center",
                  fontSize: "14px",
                  marginBottom: "1.5rem",
                }}
              >
                After you verify the microdeposits, your payment will process
                automatically. ACH payments typically take 1-3 business days to
                complete.
              </Text>
            </>
          )}

          {statusConfig.showRetryButton && (
            <Button
              type="primary"
              size="large"
              onClick={() => navigate(`/rent-payment/${id}`)}
              style={{
                width: "100%",
                height: "48px",
                marginBottom: "1rem",
              }}
            >
              Try Again
            </Button>
          )}

          <Space
            direction="vertical"
            style={{ width: "100%", marginTop: "1.5rem" }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Text strong>Payment Amount:</Text>
              <Text>${displayAmount.toFixed(2)}</Text>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Text strong>Status:</Text>
              <Tag
                color={
                  statusConfig.type === "success"
                    ? "success"
                    : statusConfig.type === "error"
                    ? "error"
                    : "processing"
                }
              >
                {effectiveStatus === "succeeded"
                  ? "Paid"
                  : effectiveStatus === "processing"
                  ? "Processing"
                  : effectiveStatus === "requires_action"
                  ? "Verification Required"
                  : effectiveStatus === "payment_failed"
                  ? "Failed"
                  : payment.status || "Unknown"}
              </Tag>
            </div>
            {payment.stripePaymentIntentId && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Text strong>Payment ID:</Text>
                <Text code style={{ fontSize: "12px" }}>
                  {payment.stripePaymentIntentId.slice(0, 20)}...
                </Text>
              </div>
            )}
          </Space>

          <Button
            type="default"
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={refreshing}
            style={{ width: "100%", marginTop: "1.5rem" }}
          >
            Refresh Status
          </Button>

          <Button
            type="link"
            onClick={() => navigate("/")}
            style={{ width: "100%", marginTop: "0.5rem" }}
          >
            Back to Home
          </Button>
        </StatusCard>
      </Content>
    </Container>
  );
};

export default RentPaymentStatus;
